import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { useParams, useLocation } from 'react-router-dom';
import { Alert, Tooltip } from 'reactstrap';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import Moment from 'react-moment';

// COMPONENTS
import Button from '../../components/Button';
import SingleSelect from '../../components/SingleSelect';
import CircleImage from '../../components/CircleImage';
import DisplayDateTime from '../../components/DisplayDateTime';
import GlobalModal from '../../components/GlobalModal';
import DetailModal from './DetailModal';
import ApproveModal from './ApproveModal';
import DenyModal from './DenyModal';
import DisplayTimeFromNow from '../../components/DisplayTimeFromNow';
import ProfileLinkedAccounts from '../../components/ProfileLinkedAccounts';
import LoadingSpinner from '../../components/LoadingSpinner';
import DisplayDate from '../../components/DisplayDate';

// ICONS
import { ReactComponent as InfoIcon } from '../../assets/svg/Information.svg';
import { ReactComponent as WarningIcon } from '../../assets/svg/alert-circle-red.svg';

// TYPES
import { RouteParams } from '../../Interfaces';
import {
	RequestsToJoinFetchCertification,
	RequestsToJoinFetchProfile,
	RequestsToJoinOrganisationAdminCommand,
	DomainProfileType,
	DomainCertificationType
} from '../../api/models';

export interface RequiredValidationCertificationsStatus extends RequestsToJoinFetchCertification {
	isRegistrationConfirmed?: boolean;
}

const RequestsToJoinDetail: React.FC = () => {
	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// All Error state
	const [showStaffRoleError, setShowStaffRoleError] = React.useState<string>('');

	const [showAdminStatusError, setShowAdminStatusError] = React.useState<string>('');

	const [showDenyActionError, setShowDenyActionError] = React.useState<string>('');

	const [showApproveActionError, setShowApproveActionError] = React.useState<string>('');

	// Check if volunteer request
	const { state } = useLocation();
	const isVolunteer = state && state.volunteer;
	const isStaff = state && state.isStaff;

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get all Staff types from Redux store
	const staffTypes = useAppSelector((state) => state.lists.staffTypes);

	const initialDetailData: RequestsToJoinFetchProfile = {
		id: undefined,
		profileImage: undefined,
		firstName: undefined,
		lastName: undefined,
		dateOfBirth: undefined,
		email: undefined,
		requested: undefined,
		canUpdate: undefined,

		staffType: undefined,
		isOrganisationAdmin: undefined,

		linkedAccounts: [],
		certifications: []
	};

	const [profileData, setProfileData] = React.useState<RequestsToJoinFetchProfile>(initialDetailData);

	// getting the id
	const params = useParams<RouteParams>();
	const requestToJoinId = params.id;

	// function to  Get staff profile data from api call
	const getRequestsToJoinDetailData = async () => {
		setIsLoading(true);

		const response = await apibridge.getRequestsToJoinDetailData(requestToJoinId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialDetailData } = response;

			setIsLoading(false);

			setProfileData(result);
		}
	};

	// Setting data getting from api to profileData state
	React.useEffect(() => {
		getRequestsToJoinDetailData();
	}, []);

	const {
		id,
		profileImage,
		firstName,
		lastName,
		dateOfBirth,
		email,
		phoneNumber,
		requested,
		canUpdate,

		staffType,
		isOrganisationAdmin,
		profileType,
		isPastMember,
		leftDate,

		linkedAccounts,
		certifications
	} = profileData;

	const [adminTooltipOpen, setAdminTooltipOpen] = React.useState<boolean>(false);
	const toggleAdminTooltip = () => setAdminTooltipOpen(!adminTooltipOpen);

	// State and Logic to manage Organisation Role
	const [selectedRole, setSelectedRole] = React.useState<string | undefined>(staffType);

	React.useEffect(() => {
		setSelectedRole(staffType);
	}, [staffType]);

	const assignRequestsToJoinOrganisationRole = async (id: string, staffType: string) => {
		setIsLoading(true);

		const response = await apibridge.assignRequestsToJoinOrganisationRole({ id, staffType });

		if (response.isError) {
			setIsLoading(false);

			setShowStaffRoleError(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);
		}
	};

	const changeRoleHandler = (val: string) => {
		setSelectedRole(val);

		assignRequestsToJoinOrganisationRole(requestToJoinId, val);
	};

	// State and Logic to manage isOrganisationAdmin
	const [isChildawareAdmin, setIsChildawareAdmin] = React.useState<boolean | undefined>(isOrganisationAdmin);

	React.useEffect(() => {
		setIsChildawareAdmin(isOrganisationAdmin);
	}, [isOrganisationAdmin]);

	const assignRequestsToJoinOrganisationAdminStatus = async (id: string, isOrganisationAdmin: boolean | undefined) => {
		const data: RequestsToJoinOrganisationAdminCommand = {
			profileId: id,
			isOrganisationAdmin
		};

		const response = await apibridge.assignRequestsToJoinOrganisationAdminStatus(data);

		if (response.isError) {
			setShowAdminStatusError(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		}
	};

	const isChildawareAdminHandler = () => {
		setIsLoading(true);

		setIsChildawareAdmin(!isChildawareAdmin);

		assignRequestsToJoinOrganisationAdminStatus(requestToJoinId, !isChildawareAdmin);

		setIsLoading(false);
	};

	// State to manage Modal
	const [showDetailModal, setShowDetailModal] = React.useState<boolean>(false);
	const [showApproveModal, setShowApproveModal] = React.useState<boolean>(false);
	const [showDenyModal, setShowDenyModal] = React.useState<boolean>(false);

	const [modalDetail, setModalDetail] = React.useState<RequestsToJoinFetchCertification>({});

	const detailModalControl = (item: RequestsToJoinFetchCertification) => {
		if (item !== undefined) {
			setModalDetail(item);

			setShowDetailModal(true);
		}
	};

	// Confirm Identity validation
	const [identityConfirmed, setIdentityConfirmed] = React.useState<boolean>(false);

	React.useEffect(() => {
		!isStaff && setIdentityConfirmed(true);
	}, []);

	// Function to decide if a document need attention or not
	const isActionRequired = (item: any) => {
		if (
			item.certificationType === DomainCertificationType.WorkingWithChildrenCheck &&
			item.isRegistrationConfirmed === false
		) {
			return true;
		} else if (
			item.certificationType === DomainCertificationType.TeacherRegistration &&
			item.isRegistrationConfirmed === false
		) {
			return true;
		} else {
			return false;
		}
	};

	// Logic to check if there is Working With Children Check or Teacher Registration in the certification from backend, and if yes each needs to be validated by using the disabled condition in the Approve Button.
	const workingWithChildrenCheckCertificate = certifications?.filter(
		(item) => item.certificationType === DomainCertificationType.WorkingWithChildrenCheck
	);

	const isWorkingWithChildrenCheckPresent =
		workingWithChildrenCheckCertificate && workingWithChildrenCheckCertificate.length > 0;

	const teacherRegistrationCertificate = certifications?.filter(
		(item) => item.certificationType === DomainCertificationType.TeacherRegistration
	);

	const isTeacherRegistrationPresent = teacherRegistrationCertificate && teacherRegistrationCertificate.length > 0;

	// Separate Teacher Registration and Working With Children Checks from other Certifications
	const otherCertifications = certifications?.filter(
		(item) =>
			item.certificationType !== DomainCertificationType.TeacherRegistration &&
			item.certificationType !== DomainCertificationType.WorkingWithChildrenCheck
	);

	const requiredValidationCertifications = certifications?.filter(
		(item) =>
			item.certificationType === DomainCertificationType.TeacherRegistration ||
			item.certificationType === DomainCertificationType.WorkingWithChildrenCheck
	);

	// Add in isRegistrationConfirmed value pair to the initial requiredValidationCertificationsStatus
	const requiredValidationInitialStatus = requiredValidationCertifications?.map((item) => {
		return {
			...item,
			isRegistrationConfirmed: false
		};
	});

	// State to control the isRegistrationConfirmed
	const [requiredValidationCertificationsStatus, setRequiredValidationCertificationsStatus] = React.useState<
		RequiredValidationCertificationsStatus[] | undefined
	>(requiredValidationInitialStatus);

	React.useEffect(() => {
		setRequiredValidationCertificationsStatus(requiredValidationInitialStatus);
	}, [certifications]);

	const areAllTeacherRegistrationConfirmed = () => {
		if (
			requiredValidationCertificationsStatus &&
			requiredValidationCertificationsStatus?.filter((item) => item.isRegistrationConfirmed === false).length > 0
		) {
			return false;
		} else {
			return true;
		}
	};

	return (
		<div className="general-form mtop-0">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<div className="general-form-layout-wrapper">
								<div className="form-main w-100">
									{/* DETAIL SUMMARY */}
									<article className="staff-profile-summary">
										<div className="summary-left">
											<CircleImage src={profileImage} firstName={firstName} lastName={lastName} isColorBorder={true} />
										</div>

										<div className="summary-center">
											<h1 className="staff-name">
												{firstName} {lastName}
											</h1>

											<div className="staff-detail">
												<div className="detail-left">
													<div className="general-sub-title">Date of birth</div>
													<div className="detail-content">
														<DisplayDate>{dateOfBirth}</DisplayDate>
													</div>
												</div>

												<div className={`detail-middle ${phoneNumber ? null : 'no-border'}`}>
													<div className="general-sub-title">Email</div>
													<div className="detail-content">{email}</div>
												</div>

												{phoneNumber && (
													<div className="detail-middle no-border">
														<div className="general-sub-title">Phone number</div>
														<div className="detail-content">{phoneNumber}</div>
													</div>
												)}
											</div>

											<div className="staff-detail">
												<div className="detail-left new-line">
													<div className="general-sub-title">Date Requested</div>
													<div className="detail-content">
														<DisplayDateTime>{requested}</DisplayDateTime>, &nbsp;
														<DisplayTimeFromNow>{requested}</DisplayTimeFromNow>
													</div>
												</div>
											</div>
										</div>
									</article>

									{isPastMember && (
										<Alert color="danger" className="mtop-40">
											<div className="alert-icon">
												<WarningIcon />
											</div>

											<div className="alert-content">
												<p>
													<strong>Please note.</strong>
												</p>
												<p className="body-small">
													This person previously had a role at this organisation. Their documents and data may have been updated since they left. Please check these documents carefully before approving the user.
												</p>
												<p className="body-small">
													<strong>
														Exit date:{' '}
														<span className="d-inline-block ml-3">
															{/* This one uses slightly different formatting, so can't use <DisplayDateTime />... */}
															<Moment format="DD/MM/YYYY, hh:mma" parse="YYYY-MM-DDTHH:mm:ss.SSSSSSSZ">
																{leftDate}
															</Moment>
														</span>
													</strong>
												</p>
											</div>
										</Alert>
									)}

									{/* ORGANISATION ROLES */}

									{canUpdate && isStaff && (
										<section className="extra-details">
											<div className="detail-left">
												<label htmlFor="roleInput" className="extra-detail-title">
													Staff role
												</label>
											</div>

											<div className="detail-right">
												<SingleSelect
													value={selectedRole}
													options={staffTypes}
													onChange={(val: string) => changeRoleHandler(val)}
													isDisabled={isOrganisationActive === false}
												/>

												{!!showStaffRoleError ? (
													<div className="form-validation-error">{showStaffRoleError}</div>
												) : null}
											</div>
										</section>
									)}

									{/* LEADER PERMISSION */}
									{canUpdate &&
										isStaff &&
										(profileType === DomainProfileType.SuperAdministrator ||
											profileType === DomainProfileType.Staff) && (
											<section className="extra-details">
												<div className="detail-left">
													<h2 className="extra-detail-title">Assign Leader Permissions</h2>

													<p className="general-sub-title">You can also edit this later on their staff profile page.</p>
												</div>

												<div className="detail-right">
													<div className="toggle-section">
														<h3 className="toggle-label">
															Assign as Leader
															<InfoIcon className="info-icon" id="childaware-admin-description-tooltip" />
															<Tooltip
																placement="top"
																isOpen={adminTooltipOpen}
																autohide={false}
																target="childaware-admin-description-tooltip"
																toggle={toggleAdminTooltip}
															>
																<div className="tooltip-content">
																	Organisation Leaders will be responsible for managing complaints and feedback sent
																	through ChildAware. They will be listed on the Leadership page for all organisation
																	members to see.
																</div>
															</Tooltip>
														</h3>

														<Button
															className={`toggle-button ${isChildawareAdmin ? 'active' : ''}`}
															onClick={isChildawareAdminHandler}
															disabled={isOrganisationActive === false}
														>
															<div className="controller"></div>
														</Button>
													</div>

													{!!showAdminStatusError ? (
														<div className="form-validation-error">{showAdminStatusError}</div>
													) : null}
												</div>
											</section>
										)}

									<ProfileLinkedAccounts linkedAccounts={linkedAccounts} />

									<section className="info-section">
										{/* Teacher Registration and WWCC Certification */}
										{requiredValidationCertificationsStatus && requiredValidationCertificationsStatus.length > 0 && (
											<>
												{requiredValidationCertificationsStatus.map((item, index) => {
													return (
														<div key={index} className="row-container">
															<div className="row detail-row">
																<div className="col-7 col-md-9 col-lg-10 pdl-0">
																	<div className="row detail-info">
																		<div className="col-lg-4 pdl-0 document-name">
																			{item.certificationTypeDisplayName}
																		</div>

																		<div className="col-lg-8 document-info">{item.summary}</div>
																	</div>
																</div>

																<div className="col-5 col-md-3 col-lg-2 link-group pdr-0">
																	<Button
																		className={`btn-text pdr-0 ${isActionRequired(item) ? 'attention' : null}`}
																		onClick={() => detailModalControl(item)}
																	>
																		{isActionRequired(item) ? 'Action required' : 'View details'}
																	</Button>
																</div>
															</div>
														</div>
													);
												})}
											</>
										)}

										{/* Other Certifications */}
										{otherCertifications && otherCertifications.length > 0 && (
											<>
												{otherCertifications.map((item, index) => {
													return (
														<div key={index} className="row-container">
															<div className="row detail-row">
																<div className="col-7 col-md-9 col-lg-10 pdl-0">
																	<div className="row detail-info">
																		<div className="col-lg-4 pdl-0 document-name">
																			{item.certificationTypeDisplayName}
																		</div>

																		<div className="col-lg-8 document-info">{item.summary}</div>
																	</div>
																</div>

																<div className="col-5 col-md-3 col-lg-2 link-group pdr-0">
																	<Button
																		className={`btn-text pdr-0 ${isActionRequired(item) ? 'attention' : null}`}
																		onClick={() => detailModalControl(item)}
																	>
																		{isActionRequired(item) ? 'Action required' : 'View details'}
																	</Button>
																</div>
															</div>
														</div>
													);
												})}
											</>
										)}
									</section>

									{canUpdate && isOrganisationActive && (
										<>
											{isStaff && (
												<>
													<div className="form-checkbox-group">
														<label className="form-control-checkbox" htmlFor="confirmId">
															<input
																checked={identityConfirmed}
																onChange={() => setIdentityConfirmed(!identityConfirmed)}
																type="checkbox"
																name="confirmId"
																id="confirmId"
																title="confirm Id"
															/>
															I have confirmed this person’s identity by comparing their actual identity with photo
															identification
														</label>
													</div>

													<div className="general-divider"></div>
												</>
											)}

											<div className="form-two-button-group align-right">
												<Button className="btn-secondary" onClick={() => setShowDenyModal(true)}>
													{isVolunteer ? 'Deny Volunteer' : 'Deny member'}
												</Button>

												<Button
													disabled={
														identityConfirmed === false ||
														((isWorkingWithChildrenCheckPresent || isTeacherRegistrationPresent) &&
															areAllTeacherRegistrationConfirmed() === false)
													}
													className="btn-primary"
													onClick={() => setShowApproveModal(true)}
												>
													{isVolunteer ? 'Approve Volunteer' : 'Approve member'}
												</Button>
											</div>

											{!!showDenyActionError ? (
												<div className="form-validation-error text-align-right mtop-8">{showDenyActionError}</div>
											) : null}

											{!!showApproveActionError ? (
												<div className="form-validation-error text-align-right mtop-8">{showApproveActionError}</div>
											) : null}
										</>
									)}

									<GlobalModal isOpen={showDetailModal} setIsOpen={setShowDetailModal}>
										<DetailModal
											profileData={profileData}
											modalDetail={modalDetail}
											requiredValidationCertificationsStatus={requiredValidationCertificationsStatus}
											setRequiredValidationCertificationsStatus={setRequiredValidationCertificationsStatus}
										/>
									</GlobalModal>

									<GlobalModal isOpen={showApproveModal} setIsOpen={setShowApproveModal} titleText="Approve request">
										<ApproveModal
											id={id}
											firstName={firstName}
											lastName={lastName}
											isVolunteer={isVolunteer}
											confirmedIdentity={identityConfirmed}
										/>
									</GlobalModal>

									<GlobalModal isOpen={showDenyModal} setIsOpen={setShowDenyModal} titleText="Deny request">
										<DenyModal id={id} firstName={firstName} lastName={lastName} isVolunteer={isVolunteer} />
									</GlobalModal>
								</div>
							</div>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default RequestsToJoinDetail;
